/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import ReactDOM from 'react-dom'

import { View } from '@components/atoms'
import {
	mono,
	monoFont,
	monoForm,
	monoShared,
} from '@components/systems/monolith/monolith.tokens'
import { MONOLITH_NAV_HEIGHT } from '@components/systems/monolith/MonolithNav'
import { DeviceEntryType } from '@src/configs/devices.generated'
import { DeviceDetailType } from './deviceDetail.types'
import { downloadDeviceDocument } from './deviceDocument'
import { Analytics } from '@utils'

export type {
	DeviceCcRowType,
	DeviceSysexRowType,
	DeviceDetailType,
} from './deviceDetail.types'

/** `<body>` 스크롤 잠금 클래스 — `MonolithConfirmModal` 과 같은 규칙(`index.css`). */
const BODY_LOCK_CLASS = 'mnModalLock'

type LoadStateType =
	| { status: 'loading' }
	| { status: 'ready'; data: DeviceDetailType }
	| { status: 'error' }

interface PropsType {
	manufacturer: string
	device: DeviceEntryType
	onClose: () => void
}

const fmt = (n: number) => n.toLocaleString('en-US')

/** 한 번 받은 장비는 세션 안에서 다시 받지 않는다. */
const cache = new Map<string, DeviceDetailType>()

/** 검색어를 공백으로 끊는다 — 조각 전부를 만족하는 행만 남는다(AND). */
const tokenize = (query: string) =>
	query
		.trim()
		.toLowerCase()
		.split(/\s+/)
		.filter(aToken => aToken !== '')

/**
 * 한 행이 검색어에 걸리는지. 값 열(Range)은 보지 않는다 — "0–127" 이 거의 모든 행에 있어
 * 걸러지지 않는다. CC 번호는 숫자만 입력했을 때 정확히 일치시킨다 — "7" 이 17 · 70 · 107 을
 * 함께 끌고 오면 번호로 찾는 뜻이 사라진다. 번호가 아닌 원문("User-defined")은 부분 일치.
 */
const hits = (tokens: string[], text: string, cc?: number | string) => {
	const hay = text.toLowerCase()
	const ccText = cc === undefined ? '' : String(cc).toLowerCase()
	return tokens.every(aToken => {
		if (hay.includes(aToken)) return true
		if (/^\d+$/.test(aToken)) return ccText === aToken
		return ccText.includes(aToken)
	})
}

const detailUrl = (slug: string) =>
	`${process.env.PUBLIC_URL || ''}/works-with/${slug}.json`

/**
 * Lucide `download` (ISC) 원본 그대로 — 24 viewBox, 세 path.
 * Support 의 셰브론과 같은 규칙: strokeWidth 만 1 로 낮춘다(DESIGN_SYSTEM §9).
 */
const DownloadIcon = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width={20}
		height={20}
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth={1}
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
		<path d="m7 10 5 5 5-5" />
		<path d="M12 15V3" />
	</svg>
)

/** Lucide `x` (ISC) 원본 그대로 — 24 viewBox, path "M18 6 6 18" · "m6 6 12 12". */
const CloseIcon = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width={28}
		height={28}
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth={1}
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<path d="M18 6 6 18" />
		<path d="m6 6 12 12" />
	</svg>
)

/**
 * 장비 상세 모달 — Works with 의 칩을 누르면 열리는 전체 화면. 하위 페이지를 두지 않는다는
 * 규칙(DESIGN_SYSTEM §12.1) 안에서 CC · SysEx 표를 보여 주는 자리다. URL 은 `/works-with#{slug}`
 * 그대로 — 페이지가 해시를 읽어 이 모달을 열고, 뒤로가기가 해시를 지우면 닫힌다. 이 컴포넌트는
 * 히스토리를 만지지 않는다 — `onClose` 를 부를 뿐이고 무엇을 할지는 페이지가 정한다.
 *
 * 상단 바의 Download PDF 는 같은 데이터를 `deviceDocument.ts` 로 넘겨 A4 문서를 굽는다 — 화면을
 * 그림으로 찍는 게 아니라 값에서 다시 조판한다(SITEMAP_PLAN §22.11).
 *
 * 문법은 `MonolithConfirmModal` 과 같다: `role="dialog"` · Tab 순환 · Escape = 닫기 · `body.mnModalLock`
 * 스크롤 잠금. 다른 점은 스크림이 아니라 페이지 그라운드(`bg`)를 꽉 채운 "한 장" 이라는 것 —
 * 표가 100 행을 넘는 장비가 많아 패널 안 스크롤보다 화면 스크롤이 맞다. 상단 바만 sticky.
 *
 * 데이터는 열릴 때 `public/works-with/{slug}.json` 하나를 받는다(빌드 때 S3 에서 구움).
 * 본문 4:8 — 좌 섹션 제목(Control Change · System Exclusive) + 항목 수, 우 헤어라인 표.
 */
export const MonolithDeviceModal = (props: PropsType) => {
	const { manufacturer, device, onClose } = props

	const panelRef = React.useRef<HTMLDivElement>(null)
	const lastFocusedRef = React.useRef<Element | null>(null)
	const [pdfState, setPdfState] = React.useState<'idle' | 'working' | 'error'>('idle')
	const [ccQuery, setCcQuery] = React.useState('')
	const [sxQuery, setSxQuery] = React.useState('')
	const [state, setState] = React.useState<LoadStateType>(() => {
		const hit = cache.get(device.slug)
		return hit ? { status: 'ready', data: hit } : { status: 'loading' }
	})

	// 스크롤 잠금 + 포커스 이동. 닫힐 때 원래 스크롤·포커스로 돌아간다.
	React.useEffect(() => {
		const scrollY = window.scrollY

		lastFocusedRef.current = document.activeElement
		document.body.classList.add(BODY_LOCK_CLASS)
		document.body.style.top = `-${scrollY}px`
		panelRef.current?.focus()

		return () => {
			document.body.classList.remove(BODY_LOCK_CLASS)
			document.body.style.top = ''
			window.scrollTo(0, scrollY)

			const last = lastFocusedRef.current
			if (last instanceof HTMLElement) {
				last.focus()
			}
		}
	}, [])

	// 장비가 바뀌면 다시 받는다. 언마운트·교체 시 진행 중인 요청은 버린다.
	React.useEffect(() => {
		const hit = cache.get(device.slug)
		if (hit) {
			setState({ status: 'ready', data: hit })
			return undefined
		}

		const controller = new AbortController()
		setState({ status: 'loading' })

		fetch(detailUrl(device.slug), { signal: controller.signal })
			.then(res => {
				if (!res.ok) throw new Error(`${res.status}`)
				return res.json() as Promise<DeviceDetailType>
			})
			.then(data => {
				cache.set(device.slug, data)
				setState({ status: 'ready', data })
			})
			.catch((err: unknown) => {
				if (err instanceof DOMException && err.name === 'AbortError') return
				setState({ status: 'error' })
			})

		return () => controller.abort()
	}, [device.slug])

	// 장비가 바뀌면 맨 위로.
	React.useEffect(() => {
		panelRef.current?.scrollTo(0, 0)
	}, [device.slug])

	// 장비가 바뀌면 다운로드 상태·검색어를 되돌린다.
	React.useEffect(() => {
		setPdfState('idle')
		setCcQuery('')
		setSxQuery('')
	}, [device.slug])

	const onDownload = () => {
		if (state.status !== 'ready' || pdfState === 'working') return
		setPdfState('working')
		const detail = state.data
		downloadDeviceDocument(detail)
			.then(() => {
				setPdfState('idle')
				Analytics.download('download_device_sheet', {
					device_manufacturer: detail.manufacturer,
					device_name: detail.name,
				})
			})
			.catch((err: unknown) => {
				// 조용히 삼키면 다음에 왜 실패했는지 알 길이 없다 — 딕셔너리 문자열이 원인일 수 있다.
				console.error('[works-with] PDF 생성 실패', err)
				setPdfState('error')
			})
	}

	const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
		if (e.key === 'Escape') {
			e.preventDefault()
			onClose()
			return
		}
		if (e.key !== 'Tab') return

		const panel = panelRef.current
		if (panel === null) return

		const focusable = panel.querySelectorAll<HTMLElement>(
			'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
		)
		if (focusable.length === 0) return

		const first = focusable[0]
		const last = focusable[focusable.length - 1]
		const active = document.activeElement

		if (e.shiftKey && (active === first || active === panel)) {
			e.preventDefault()
			last.focus()
		} else if (!e.shiftKey && active === last) {
			e.preventDefault()
			first.focus()
		}
	}

	const data = state.status === 'ready' ? state.data : null
	const ccRows = data?.cc ?? []
	const sysexRows = data?.sysex ?? []
	const ccHasDesc = ccRows.some(aRow => aRow.description !== '')
	const ccHasRange = ccRows.some(aRow => aRow.range !== '')
	const sxHasRange = sysexRows.some(aRow => aRow.range !== '')

	// 열 구성(ccHasRange · ccHasDesc · sxHasRange)은 전체 행에서 정한다 — 검색 중에 열이
	// 나타났다 사라지면 표가 타자마다 흔들린다.
	const ccShown = React.useMemo(() => {
		const tokens = tokenize(ccQuery)
		if (tokens.length === 0) return ccRows
		return ccRows.filter(aRow =>
			hits(tokens, `${aRow.name} ${aRow.description}`, aRow.cc),
		)
	}, [ccRows, ccQuery])

	const sxShown = React.useMemo(() => {
		const tokens = tokenize(sxQuery)
		if (tokens.length === 0) return sysexRows
		return sysexRows.filter(aRow =>
			hits(tokens, `${aRow.name} ${aRow.description} ${aRow.message}`),
		)
	}, [sysexRows, sxQuery])

	const updated = data ? (device.cc > 0 ? data.updated.cc : data.updated.sysex) : null

	// body 에 포털로 붙인다 — HomeLayout 의 `.containerInnerView`(z 3)가 스택 컨텍스트를 만들어
	// 페이지 안에서는 z-index 를 아무리 올려도 네비(z 5) 아래에 깔린다.
	return ReactDOM.createPortal(
		<View
			css={style}
			ref={panelRef}
			tabIndex={-1}
			role="dialog"
			aria-modal="true"
			aria-labelledby="mnDeviceTitle"
			onKeyDown={onKeyDown}
		>
			<View className="mnDevBar">
				<View className="mnWrap mnDevBarIn">
					<span className="mnDevBarName">
						Works with · {manufacturer} {device.name}
					</span>
					<View className="mnDevActions">
						{state.status === 'ready' && (
							<button
								type="button"
								className="mnDevAction"
								onClick={onDownload}
								disabled={pdfState === 'working'}
								aria-label="Download PDF"
							>
								<span className="mnDevActionLabel">
									{pdfState === 'working'
										? 'Preparing'
										: pdfState === 'error'
										? 'Download failed'
										: 'Download PDF'}
								</span>
								<DownloadIcon />
							</button>
						)}
						<button
							type="button"
							className="mnDevAction"
							onClick={onClose}
							aria-label="Close"
						>
							<span className="mnDevActionLabel">Close</span>
							<CloseIcon />
						</button>
					</View>
				</View>
			</View>

			<View className="mnWrap">
				<View className="mnDevHead">
					<span className="mnKicker">{manufacturer}</span>
					<h2 className="mnDevTitle" id="mnDeviceTitle">
						{device.name}
					</h2>
					<View className="mnDevFacts">
						<span className="mnMeta">{fmt(device.cc)} CC</span>
						<span className="mnMeta">{fmt(device.sysex)} SysEx</span>
						{updated !== null && <span className="mnMeta">Updated {updated}</span>}
					</View>
				</View>

				{state.status === 'loading' && (
					<View className="mnDevState">
						<span className="mnMeta">Loading</span>
					</View>
				)}

				{state.status === 'error' && (
					<View className="mnDevState">
						<p>
							The parameter list for this device did not load. Reload the page to try
							again.
						</p>
					</View>
				)}

				{ccRows.length > 0 && (
					<View className="mnDevSection">
						<View className="mnDevSectionHead">
							<h3 className="mnDevSectionTitle">Control Change</h3>
							<span className="mnMeta">
								{ccShown.length === ccRows.length
									? `${fmt(ccRows.length)} ${ccRows.length === 1 ? 'entry' : 'entries'}`
									: `${fmt(ccShown.length)} of ${fmt(ccRows.length)} entries`}
							</span>
						</View>
						<View className="mnDevBody">
							<View className="mnField mnDevSearch">
								<label className="mnLabel" htmlFor="mnCcSearch">
									Search
								</label>
								<input
									id="mnCcSearch"
									type="search"
									className="mnInput"
									placeholder="CC number, parameter, description"
									value={ccQuery}
									onChange={e => setCcQuery(e.target.value)}
									autoComplete="off"
									spellCheck={false}
								/>
							</View>
							{ccShown.length === 0 ? (
								<p className="mnDevEmpty">
									No Control Change parameter matches that search.
								</p>
							) : (
								<View className="mnTableWrap">
									<table className="mnTable">
										<thead>
											<tr>
												<th scope="col">CC</th>
												<th scope="col">Parameter</th>
												{ccHasRange && <th scope="col">Range</th>}
												{ccHasDesc && <th scope="col">Description</th>}
											</tr>
										</thead>
										<tbody>
											{ccShown.map((aRow, idx) => (
												<tr key={`${aRow.cc}-${aRow.name}-${idx}`}>
													<td className="num">{aRow.cc}</td>
													<td className="name">{aRow.name}</td>
													{ccHasRange && <td className="range">{aRow.range}</td>}
													{ccHasDesc && <td className="desc">{aRow.description}</td>}
												</tr>
											))}
										</tbody>
									</table>
								</View>
							)}
						</View>
					</View>
				)}

				{sysexRows.length > 0 && (
					<View className="mnDevSection">
						<View className="mnDevSectionHead">
							<h3 className="mnDevSectionTitle">System Exclusive</h3>
							<span className="mnMeta">
								{sxShown.length === sysexRows.length
									? `${fmt(sysexRows.length)} ${
											sysexRows.length === 1 ? 'entry' : 'entries'
									  }`
									: `${fmt(sxShown.length)} of ${fmt(sysexRows.length)} entries`}
							</span>
						</View>
						<View className="mnDevBody">
							<View className="mnField mnDevSearch">
								<label className="mnLabel" htmlFor="mnSxSearch">
									Search
								</label>
								<input
									id="mnSxSearch"
									type="search"
									className="mnInput"
									placeholder="Parameter, description, message"
									value={sxQuery}
									onChange={e => setSxQuery(e.target.value)}
									autoComplete="off"
									spellCheck={false}
								/>
							</View>
							{sxShown.length === 0 ? (
								<p className="mnDevEmpty">
									No System Exclusive parameter matches that search.
								</p>
							) : (
								<View className="mnTableWrap">
									<table className="mnTable">
										<thead>
											<tr>
												<th scope="col">Parameter</th>
												{sxHasRange && <th scope="col">Range</th>}
												<th scope="col">Message</th>
											</tr>
										</thead>
										<tbody>
											{sxShown.map((aRow, idx) => (
												<tr key={`${aRow.name}-${idx}`}>
													{/* SysEx 설명은 주소 바이트 + 짧은 라벨이라 열을 따로 두지 않고 이름 아래 한 줄로 — 메시지 열이 넓어 4열이면 잘린다 */}
													<td className="name">
														{aRow.name}
														{aRow.description !== '' && (
															<span className="mnDevDesc">{aRow.description}</span>
														)}
													</td>
													{sxHasRange && <td className="range">{aRow.range}</td>}
													<td className="hex">{aRow.message}</td>
												</tr>
											))}
										</tbody>
									</table>
								</View>
							)}
						</View>
					</View>
				)}

				{data !== null && (
					<View className="mnDevNote">
						<p>
							Values are as the device documents them. Any of them goes out from a
							footswitch, an expression pedal, or a scene.
						</p>
					</View>
				)}
			</View>
		</View>,
		document.body,
	)
}

const style = css([
	monoShared,
	monoForm,
	{
		position: 'fixed',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		zIndex: 1400,
		overflowY: 'auto',
		// 세로만 가둔다. 양축을 `contain` 으로 잡으면 가로 오버스크롤이 브라우저로 안 넘어가서
		// macOS 두 손가락 스와이프·모바일 가장자리 스와이프의 **뒤로가기 제스처가 죽는다**.
		overscrollBehaviorY: 'contain',
		overscrollBehaviorX: 'auto',
		background: mono.bg,
		fontFamily: monoFont.sans,
		outline: 'none',
		WebkitOverflowScrolling: 'touch',

		// 상단 바 — 네비와 같은 높이. 표가 길어도 Close 가 항상 보인다.
		'.mnDevBar': {
			position: 'sticky',
			top: 0,
			// 표 위 검색(sticky, z 1)보다 위 — 바의 밑줄이 입력에 덮이지 않는다.
			zIndex: 2,
			height: MONOLITH_NAV_HEIGHT,
			background: mono.bg,
			borderBottom: `1px solid ${mono.line}`,
		},
		'.mnDevBarIn': {
			height: '100%',
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'space-between',
			gap: 24,
		},
		'.mnDevBarName': {
			minWidth: 0,
			overflow: 'hidden',
			textOverflow: 'ellipsis',
			whiteSpace: 'nowrap',
			fontSize: 12,
			letterSpacing: '.2em',
			textTransform: 'uppercase',
			fontWeight: 500,
			color: mono.faint,
		},
		// 상단 바 액션 — Download PDF · Close. 둘 다 텍스트 + 아이콘, 블랙 CTA 는 두지 않는다(§5).
		'.mnDevActions': {
			flexShrink: 0,
			display: 'flex',
			alignItems: 'center',
			gap: 24,
		},
		'.mnDevAction': {
			display: 'inline-flex',
			alignItems: 'center',
			gap: 10,
			height: 44,
			padding: 0,
			border: 0,
			background: 'transparent',
			fontFamily: monoFont.sans,
			fontSize: 13,
			fontWeight: 500,
			color: mono.dim,
			cursor: 'pointer',
			transition: 'color 200ms ease-out',

			svg: {
				display: 'block',
			},
			'&:hover': {
				color: mono.ink,
			},
			'&:focus-visible': {
				outline: `1px solid ${mono.ink}`,
				outlineOffset: 3,
			},
			'&:disabled': {
				cursor: 'default',
				color: mono.faint,
			},
			// 좁은 화면에서는 아이콘만 — 바 이름이 잘리기 전에 액션 라벨을 접는다.
			// 라벨이 숨어도 이름은 남아야 한다 — 두 버튼 다 aria-label 을 들고 있다.
			'@media (max-width: 620px)': {
				'.mnDevActionLabel': {
					display: 'none',
				},
			},
			'@media (prefers-reduced-motion: reduce)': {
				transition: 'none',
			},
		},

		// 헤더 — 키커(제조사) · 장비명 Thin · 사실 세 개.
		'.mnDevHead': {
			padding: 'clamp(48px, 6vw, 88px) 0 clamp(40px, 4.5vw, 64px)',
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'flex-start',
			gap: 20,

			'.mnDevTitle': {
				fontSize: 'clamp(40px, 5.6vw, 80px)',
				lineHeight: 1,
				fontWeight: 100,
				letterSpacing: '-0.01em',
				color: mono.ink,
				overflowWrap: 'anywhere',
			},
			'.mnDevFacts': {
				display: 'flex',
				flexWrap: 'wrap',
				gap: '10px 28px',
				fontVariantNumeric: 'tabular-nums',
			},
		},

		'.mnDevState': {
			borderTop: `1px solid ${mono.line}`,
			padding: 'clamp(36px, 3.6vw, 52px) 0',

			p: {
				fontSize: 16,
				lineHeight: 1.7,
				fontWeight: 300,
				color: mono.dim,
				maxWidth: 620,
			},
		},

		// 섹션 — 페이지의 제조사 행과 같은 4:8. 좌 제목, 우 표.
		'.mnDevSection': {
			display: 'grid',
			gridTemplateColumns: 'minmax(0, 4fr) minmax(0, 8fr)',
			gap: 48,
			alignItems: 'start',
			padding: 'clamp(36px, 3.6vw, 52px) 0',
			borderTop: `1px solid ${mono.line}`,

			'@media (max-width: 900px)': {
				gridTemplateColumns: '1fr',
				gap: 24,
			},
		},
		// 우열 — 검색 + 표를 한 덩어리로. 섹션이 4:8 그리드라 래퍼가 하나 필요하다.
		'.mnDevBody': {
			minWidth: 0,
			display: 'flex',
			flexDirection: 'column',
			gap: 20,
		},
		// 표 위 검색 — 100 행을 넘는 표가 많아 스크롤해도 따라온다. 상단 바 바로 아래에 선다.
		// 배경은 열 전체를 덮고(행이 뒤로 지나간다) 입력만 420 으로 제한한다.
		'.mnDevSearch': {
			position: 'sticky',
			top: MONOLITH_NAV_HEIGHT,
			zIndex: 1,
			gap: 8,
			background: mono.bg,
			padding: '12px 0 14px',

			'.mnInput': {
				maxWidth: 420,
			},
		},
		'.mnDevEmpty': {
			fontSize: 15,
			lineHeight: 1.7,
			fontWeight: 300,
			color: mono.dim,
		},
		'.mnDevSectionHead': {
			position: 'sticky',
			top: MONOLITH_NAV_HEIGHT + 24,
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'flex-start',
			gap: 14,

			'.mnDevSectionTitle': {
				fontSize: 'clamp(28px, 2.8vw, 40px)',
				lineHeight: 1.1,
				fontWeight: 300,
				color: mono.ink,
			},
			'.mnMeta': {
				fontVariantNumeric: 'tabular-nums',
			},

			'@media (max-width: 900px)': {
				position: 'static',
			},
		},

		// 표 — 헤어라인 행. 좁은 화면에서는 표만 가로 스크롤(페이지는 넘치지 않는다).
		'.mnTableWrap': {
			minWidth: 0,
			overflowX: 'auto',
		},
		'.mnTable': {
			width: '100%',
			borderCollapse: 'collapse',
			minWidth: 480,

			// 전역 CSS 에 `section table/th/td { border }` 같은 규칙이 살아 있다(문서 뷰어 잔재) — 보더·배경을 명시해 끊는다.
			th: {
				textAlign: 'left',
				padding: '0 24px 14px 0',
				border: 0,
				background: 'transparent',
				borderBottom: `1px solid ${mono.line}`,
				fontSize: 12,
				letterSpacing: '.2em',
				textTransform: 'uppercase',
				fontWeight: 500,
				color: mono.faint,
				whiteSpace: 'nowrap',
			},
			td: {
				textAlign: 'left',
				padding: '13px 24px 13px 0',
				border: 0,
				background: 'transparent',
				borderBottom: `1px solid ${mono.line}`,
				verticalAlign: 'top',
				fontSize: 14,
				lineHeight: 1.5,
				fontWeight: 300,
				color: mono.dim,
			},
			'th:last-child, td:last-child': {
				paddingRight: 0,
			},
			'td.num': {
				width: 56,
				fontWeight: 500,
				fontVariantNumeric: 'tabular-nums',
				color: mono.ink,
				whiteSpace: 'nowrap',
			},
			'td.name': {
				minWidth: 160,
				fontWeight: 400,
				color: mono.ink,

				'.mnDevDesc': {
					display: 'block',
					marginTop: 4,
					fontSize: 13,
					fontWeight: 300,
					color: mono.faint,
					overflowWrap: 'anywhere',
				},
			},
			'td.range': {
				fontVariantNumeric: 'tabular-nums',
				whiteSpace: 'nowrap',
			},
			'td.hex': {
				fontFamily: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace',
				fontSize: 13,
				letterSpacing: '.02em',
				whiteSpace: 'nowrap',
				color: mono.ink,
			},
			'td.desc': {
				minWidth: 220,
				maxWidth: 480,
				overflowWrap: 'anywhere',
			},

			// 좁은 화면 — 최소폭을 풀고 hex 도 줄바꿈. 표가 잘린 채 가로 스크롤만 남는 것보다 낫다.
			'@media (max-width: 700px)': {
				minWidth: 0,

				'td.name': {
					minWidth: 120,
				},
				'td.hex': {
					minWidth: 120,
					whiteSpace: 'normal',
					overflowWrap: 'anywhere',
				},
				'td.desc': {
					minWidth: 140,
				},
			},
		},

		'.mnDevNote': {
			padding: 'clamp(40px, 5vw, 72px) 0 clamp(64px, 8.3vw, 120px)',
			borderTop: `1px solid ${mono.line}`,

			p: {
				fontSize: 15,
				lineHeight: 1.7,
				fontWeight: 300,
				color: mono.dim,
				maxWidth: 620,
			},
		},
	},
])
